// Story 5.5 — TanStack Query mutation hook for POST
// /api/v1/marketplace/listings/sealed-dossier/{listing_id}/buy.
//
// Sibling to useBuyShareListing — same shape, response carries
// `sealed_dossier_id` instead of `share_id`, vault_redirect_url is
// /vault (no per-dossier detail page at v1).

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface BuySealedDossierListingVariables {
  listing_id: string;
  /** See BuyShareListingVariables.expected_price_cents. */
  expected_price_cents?: number;
}

export interface BuySealedDossierListingFeeBreakdown {
  platform_fee_cents: number;
  creator_royalty_cents: number;
  estimated_proceeds_cents: number;
}

export interface BuySealedDossierListingResponseData {
  listing_id: string;
  sealed_dossier_id: string;
  price_cents: number;
  fee_breakdown: BuySealedDossierListingFeeBreakdown;
  balance_after_cents: number;
  vault_redirect_url: string;
}

export interface BuySealedDossierListingError {
  code: string;
  message: string;
  field?: string;
  required_cents?: number;
  available_cents?: number;
  status: number;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function postBuySealedDossierListing(
  variables: BuySealedDossierListingVariables,
): Promise<BuySealedDossierListingResponseData> {
  const qs =
    variables.expected_price_cents != null
      ? `?expected_price_cents=${variables.expected_price_cents}`
      : "";
  const response = await fetch(
    `/api/v1/marketplace/listings/sealed-dossier/${variables.listing_id}/buy${qs}`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-idempotency-key": freshIdempotencyKey(),
      },
      body: "{}",
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: BuySealedDossierListingError = {
      code: "BUY_LISTING_FAILED",
      message: `Purchase failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          required_cents: raw.error.details?.required_cents,
          available_cents: raw.error.details?.available_cents,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  const body = (await response.json()) as {
    data: BuySealedDossierListingResponseData;
  };
  return body.data;
}

export function useBuySealedDossierListing(): UseMutationResult<
  BuySealedDossierListingResponseData,
  BuySealedDossierListingError,
  BuySealedDossierListingVariables
> {
  return useMutation<
    BuySealedDossierListingResponseData,
    BuySealedDossierListingError,
    BuySealedDossierListingVariables
  >({
    mutationFn: postBuySealedDossierListing,
  });
}
